import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { selectDataType } from '../../services/types';
import { ChevronDown } from 'lucide-react';

export function Select(
   { title, data = [], isSmall, handleSelectItem, emptyMsg, isLoading = false, errorMsg, menuMinWidth = 0 }:
   {
      title?: string,
      data: selectDataType[],
      isSmall?: boolean,
      handleSelectItem: (id: any) => void,
      emptyMsg?: string,
      isLoading?: boolean,
      errorMsg?: string,
      menuMinWidth?: number,
   }
) {
   const [menuToggle, setMenuToggle] = React.useState<boolean>(false);
   const [menuPosition, setMenuPosition] = React.useState<{ top: number, left: number, width: number } | null>(null);
   const selectRef = React.useRef<HTMLDivElement>(null);
   const menuRef = React.useRef<HTMLUListElement>(null);
   const menuId = React.useId();
   const visibleData = React.useMemo(() => data.filter(item => !item.isHide), [data]);
   const hasOptions = visibleData.length > 0;
   const statusMessage = isLoading ? 'Loading...' : errorMsg || emptyMsg || 'No options available';

   // Recompute the floating menu's position from the trigger's real screen position,
   // since it's portaled to <body> and can no longer rely on a positioned ancestor.
   const updateMenuPosition = React.useCallback(() => {
      if (!selectRef.current) return;
      const rect = selectRef.current.getBoundingClientRect();
      const viewportPadding = 8;
      const estimatedHeight = Math.min(Math.max(visibleData.length * 40, 40), 194.4);
      const shouldOpenAbove = window.innerHeight - rect.bottom < estimatedHeight + 7.5 && rect.top > estimatedHeight + 7.5;
      const maxWidth = Math.max(1, window.innerWidth - viewportPadding * 2);
      const width = Math.min(Math.max(rect.width, menuMinWidth), maxWidth);
      const maxLeft = Math.max(viewportPadding, window.innerWidth - width - viewportPadding);
      const left = Math.min(Math.max(rect.left, viewportPadding), maxLeft);
      const maxTop = Math.max(viewportPadding, window.innerHeight - estimatedHeight - viewportPadding);
      const top = shouldOpenAbove
         ? Math.max(viewportPadding, rect.top - estimatedHeight - 7.5)
         : Math.min(rect.bottom + 7.5, maxTop);
      setMenuPosition({ top, left, width });
   }, [menuMinWidth, visibleData.length]);

   // ---
   React.useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
         if (
            selectRef.current && !selectRef.current.contains(e.target as Node) &&
            (!menuRef.current || !menuRef.current.contains(e.target as Node))
         ) {
            setMenuToggle(false);
         }
      };
      // Capture phase fires before any stopPropagation() in child components,
      // so the dropdown closes even inside modals that call e.stopPropagation().
      document.addEventListener('mousedown', handleClickOutside, true);
      return () => { document.removeEventListener('mousedown', handleClickOutside, true) };
   }, []);

   // Keep the floating menu aligned with its trigger while open, even if an
   // ancestor (e.g. a modal body) scrolls instead of the page itself.
   React.useEffect(() => {
      if (!menuToggle) return;
      updateMenuPosition();
      window.addEventListener('scroll', updateMenuPosition, true);
      window.addEventListener('resize', updateMenuPosition);
      return () => {
         window.removeEventListener('scroll', updateMenuPosition, true);
         window.removeEventListener('resize', updateMenuPosition);
      };
   }, [menuToggle, updateMenuPosition]);

   React.useEffect(() => {
      if (!hasOptions || isLoading || errorMsg) setMenuToggle(false);
   }, [errorMsg, hasOptions, isLoading]);

   // ---
   const itemActive = data.find(item => item.isActive);

   const toggleMenu = () => {
      if (!hasOptions || isLoading || errorMsg) return;
      setMenuToggle(menu => !menu);
   };

   const selectItem = (id: any) => {
      handleSelectItem(id);
      setMenuToggle(false);
      requestAnimationFrame(() => selectRef.current?.focus());
   };

   const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
         event.preventDefault();
         toggleMenu();
      } else if (event.key === 'Escape') {
         setMenuToggle(false);
      } else if (event.key === 'ArrowDown' && menuToggle) {
         event.preventDefault();
         (menuRef.current?.querySelector('li') as HTMLElement | null)?.focus();
      }
   };

   const handleItemKeyDown = (event: React.KeyboardEvent<HTMLLIElement>, index: number, id: any) => {
      if (event.key === 'Enter' || event.key === ' ') {
         event.preventDefault();
         selectItem(id);
      } else if (event.key === 'Escape') {
         setMenuToggle(false);
         requestAnimationFrame(() => selectRef.current?.focus());
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
         event.preventDefault();
         const nextIndex = event.key === 'ArrowDown' ? index + 1 : index - 1;
         const nextItem = menuRef.current?.querySelectorAll('li')[nextIndex] as HTMLElement | undefined;
         nextItem?.focus();
      }
   };

   return (
      <Main className="select" style={{ fontSize: isSmall ? '15px' : '18px' }}>
         { title ? <h4>{ title }</h4> : null }
         <div
            ref={ selectRef }
            onClick={ toggleMenu }
            onKeyDown={ handleTriggerKeyDown }
            role="combobox"
            aria-expanded={ menuToggle }
            aria-controls={ menuId }
            aria-haspopup="listbox"
            aria-label={ title }
            tabIndex={ hasOptions && !isLoading && !errorMsg ? 0 : -1 }
            title={ itemActive?.name || statusMessage }
         >
            { hasOptions ? <span>{ itemActive?.name || statusMessage }</span> : <span className="status">{ statusMessage }</span> }
            <ChevronDown style={{ opacity: hasOptions ? 0.5 : 0.2 }} />
         </div>
         { menuToggle && hasOptions && menuPosition ? createPortal(
            <Menu
               ref={ menuRef }
               id={ menuId }
               className="nowheel"
               role="listbox"
               aria-label={ title ? `${title} options` : 'Options' }
               style={{ top: menuPosition.top, left: menuPosition.left, width: menuPosition.width }}
            >{
               visibleData.map((item, index) => (
                  <li
                     key={ item.id }
                     onClick={ _ => selectItem(item.id) }
                     onKeyDown={ event => handleItemKeyDown(event, index, item.id) }
                     role="option"
                     aria-selected={ item.id === itemActive?.id }
                     tabIndex={ -1 }
                     title={ item.name }
                  >
                     <span>{ item.name }</span>
                     <span>{ item.id === itemActive?.id ? <FontAwesomeIcon icon={ faCheck } /> : null }</span>
                  </li>
               ))
            }</Menu>,
            document.body
         ) : null }
      </Main>
   );
}

// ---
const Main = styled.div`
   display: flex;
   align-items: center;

   h4 {
      font-weight: 400;
      margin-right: 15px;
   }
   > div {
      position: relative;
      display: flex;
      align-items: center;
      box-sizing: border-box;
      border: 1px solid rgb(200, 200, 200);
      border-radius: 10px;
      padding: 5px 10px;
      height: 40px;
      width: 100%;
      cursor: pointer;

      &:focus-visible {
         outline: 2px solid #06b6d4;
         outline-offset: 2px;
      }

      > span {
         min-width: 0;
         width: 100%;
         color: #696F74;
         margin-right: 15px;
         white-space: nowrap;
         overflow: hidden;
         text-overflow: ellipsis;

         &.status {
            opacity: 0.65;
         }
      }
   }
`;

// Rendered through a portal into <body>, so it floats above any ancestor
// with overflow:hidden/auto (e.g. a modal) instead of being clipped by it.
const Menu = styled.ul`
   position: fixed;
   z-index: 40000000;
   box-sizing: border-box;
   margin: 0;
   padding: 0;
   background-color: rgb(245, 245, 245);
   box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
   max-height: 194.4px;
   overflow: auto;
   border-radius: 10px;

   li {
      display: flex;
      align-items: center;
      justify-content: space-between;
      box-sizing: border-box;
      padding: 0 10px;
      min-width: 0;
      min-height: 40px;
      height: 40px;
      transition: all .2s ease-in-out;
      width: 100%;
      cursor: pointer;

      &:not(:last-of-type) {
         border-bottom: 1px solid rgb(215, 215, 215);
      }
      span {
         &:first-of-type {
            min-width: 0;
            opacity: 0.8;
            width: 100%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
         }
         &:last-of-type {
            flex: 0 0 auto;
            opacity: 0.5;
            margin-left: 7.5px;
         }
      }
      &:focus-visible {
         outline: 2px solid #06b6d4;
         outline-offset: -2px;
      }
      &:hover {
         background-color: #1E272E;
         color: #fff;

         span {
            opacity: 1;
         }
      }
   }
`;
